// Build the PATCH payload for a full-journal save by diffing the in-memory journal
// against the server state it was hydrated from (the baseline).
//
// The journal drawer's "Save journal" button used to PATCH the ENTIRE in-memory journal.
// If that copy was loaded before another user/window edited the same journal, every
// narrative field in the stale copy silently overwrote the newer content on the server
// (patch-journal.js $sets whatever strings the client sends — '' clears the stored text,
// and there is no updatedDate/version guard). Diagnoses/medicine survived because arrays
// go through $addToSet, which never removes. (TICKET-01303: a vet's whole visit text was
// wiped when a second session saved a two-hour-old copy while completing the sale.)
//
// Sending only fields that differ from the baseline means untouched fields can never
// clobber newer server content, while real edits — including explicit clears to '' —
// still differ from the baseline and are sent exactly as before. Without a baseline for
// this journal id we fall back to the full object (legacy behavior).

const normalize = value => (value instanceof Date ? value.toISOString() : value);

const isEqualValue = (a, b) => {
    if ((a && typeof a === 'object' && !(a instanceof Date)) || (b && typeof b === 'object' && !(b instanceof Date))) {
        try {
            return JSON.stringify(a) === JSON.stringify(b);
        } catch (e) {
            return false;
        }
    }
    return normalize(a) === normalize(b);
};

function diffJournalForSave(journal, baseline) {
    if (!journal || !journal.id) {
        return journal;
    }
    if (!baseline || baseline.id !== journal.id) {
        return { ...journal };
    }
    const diff = { id: journal.id };
    Object.keys(journal).forEach(key => {
        if (key === 'id') {
            return;
        }
        const value = journal[key];
        // undefined is stripped by the backend anyway — never send it.
        if (value === undefined) {
            return;
        }
        if (!isEqualValue(value, baseline[key])) {
            diff[key] = value;
        }
    });
    return diff;
}

// TICKET-01394: decide whether a not-yet-inserted journal carries any user content.
//
// The new-journal drawer stamps animal/visitor/sale/veterinary/date onto the shared
// journalStore.newJournal the moment it opens. Two paths then POSTed that stamped-but-
// untouched object as a real journal: (1) closing the drawer with X/backdrop (the
// save-on-close callback in listAnimalMini), and (2) a "Lagre" click while the shared
// newJournal had been blanked by another drawer mount — creating an EMPTY duplicate
// journal on a sale that already had the vet's real journal for the same animal.
// A journal where every field below is empty carries no information, so skipping the
// insert can never lose data.
//
// Fields the drawer/system sets without the vet typing anything. Everything NOT listed
// here counts as user content when it holds a meaningful value, so new journal fields
// are guarded automatically.
const STAMPED_OR_SYSTEM_FIELDS = new Set([
    '_id', '__v', 'id', 'uuidv4', 'journalNumber', 'saleNumber', 'animalNumber',
    'animal', 'visitor', 'sale', 'veterinary', 'user', 'customer',
    'date', 'closed', 'createdDate', 'updatedDate', 'version', 'offline',
    'members', 'changes', 'views',
]);

const isMeaningfulValue = value => {
    if (value === null || value === undefined) {
        return false;
    }
    if (typeof value === 'string') {
        return value.trim() !== '';
    }
    if (typeof value === 'number') {
        return Number.isFinite(value) && value !== 0;
    }
    if (typeof value === 'boolean') {
        return value;
    }
    if (value instanceof Date) {
        return true;
    }
    if (Array.isArray(value)) {
        return value.length > 0;
    }
    if (typeof value === 'object') {
        return Object.keys(value).length > 0;
    }
    return false;
};

function journalHasUserContent(journal) {
    if (!journal || typeof journal !== 'object') {
        return false;
    }
    return Object.keys(journal).some(key => {
        if (STAMPED_OR_SYSTEM_FIELDS.has(key)) {
            return false;
        }
        // Server-derived search/normalization fields are never user content.
        if (/(Norm|SearchTokens)$/.test(key)) {
            return false;
        }
        return isMeaningfulValue(journal[key]);
    });
}

export { diffJournalForSave, journalHasUserContent, STAMPED_OR_SYSTEM_FIELDS };
export default diffJournalForSave;
